import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  RestoreFromTrash as RestoreIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { recoverFlowExecution, getExecutionMetrics } from '../../api/agenticFlow.api';

function ErrorRecoveryModal({
  open,
  onClose,
  executionId,
  error,
  executionData,
  onRecoverySuccess,
  onRetry,
}) {
  const [recovering, setRecovering] = useState(false);
  const [recoveryOptions, setRecoveryOptions] = useState([]);
  const [selectedRecoveryPoint, setSelectedRecoveryPoint] = useState(null);
  const [recoveryHistory, setRecoveryHistory] = useState([]);

  useEffect(() => {
    if (open && executionId) {
      loadRecoveryOptions();
    }
  }, [open, executionId]);

  const loadRecoveryOptions = async () => {
    try {
      // Get execution metrics to understand what recovery options are available
      const metrics = await getExecutionMetrics();

      // Extract recovery points from execution data
      const recoveryPoints = [];

      if (executionData?.execution_info_list) {
        const completedNodes = executionData.execution_info_list.filter(
          (node) => node.status === 'completed',
        );

        // Add recovery points at key stages
        completedNodes.forEach((node, index) => {
          if (node.node_type === 'form' || node.node_type === 'join'
              || node.node_type === 'parallel' || index === completedNodes.length - 1) {
            recoveryPoints.push({
              id: `checkpoint_${node.node_id}`,
              nodeId: node.node_id,
              nodeType: node.node_type,
              timestamp: node.timestamp,
              description: getRecoveryPointDescription(node),
              recommended: node.node_type === 'form' || index === completedNodes.length - 1,
            });
          }
        });
      }

      setRecoveryOptions(recoveryPoints);

      // Auto-select the most recent recommended recovery point
      const recommended = recoveryPoints.find((point) => point.recommended);
      if (recommended) {
        setSelectedRecoveryPoint(recommended);
      }
    } catch (error) {
      console.error('Failed to load recovery options:', error);
    }
  };

  const getRecoveryPointDescription = (node) => {
    switch (node.node_type) {
      case 'form':
        return `After form completion - ${node.node_id}`;
      case 'join':
        return 'After parallel processing completion';
      case 'parallel':
        return 'Before parallel processing';
      case 'ai-agent':
        return 'After AI response generation';
      case 'api-call':
        return 'After API call completion';
      case 'code-executor':
        return 'After code execution';
      default:
        return `After ${node.node_type} completion`;
    }
  };

  const getErrorSeverity = (error) => {
    if (!error) return 'info';
    const errorStr = error.toString().toLowerCase();

    if (errorStr.includes('timeout') || errorStr.includes('network')) return 'warning';
    if (errorStr.includes('validation') || errorStr.includes('serialization')) return 'error';
    if (errorStr.includes('interrupt') || errorStr.includes('form')) return 'info';

    return 'error';
  };

  const getErrorCategory = (error) => {
    if (!error) return 'Unknown';
    const errorStr = error.toString().toLowerCase();

    if (errorStr.includes('timeout')) return 'Network Timeout';
    if (errorStr.includes('network') || errorStr.includes('connection')) return 'Network Error';
    if (errorStr.includes('validation')) return 'Data Validation';
    if (errorStr.includes('serialization')) return 'Data Serialization';
    if (errorStr.includes('interrupt')) return 'User Interaction';
    if (errorStr.includes('api')) return 'API Error';

    return 'System Error';
  };

  const getSuggestedActions = (error) => {
    if (!error) return [];
    const errorStr = error.toString().toLowerCase();

    const suggestions = [];

    if (errorStr.includes('timeout') || errorStr.includes('network')) {
      suggestions.push('Check your internet connection');
      suggestions.push('Try again in a few moments');
      suggestions.push('Consider using a recovery point before the network call');
    }

    if (errorStr.includes('validation')) {
      suggestions.push('Check the form data you submitted');
      suggestions.push('Recover from the last form submission');
    }

    if (errorStr.includes('api')) {
      suggestions.push('The external service may be temporarily unavailable');
      suggestions.push('Try recovering from before the API calls');
    }

    if (suggestions.length === 0) {
      suggestions.push('Try recovering from the most recent checkpoint');
      suggestions.push('Contact support if the issue persists');
    }

    return suggestions;
  };

  const handleRecovery = async () => {
    if (!selectedRecoveryPoint) return;

    setRecovering(true);

    try {
      const result = await recoverFlowExecution(executionId);

      if (result.success) {
        // Add to recovery history
        setRecoveryHistory((prev) => [...prev, {
          timestamp: new Date().toISOString(),
          recoveryPoint: selectedRecoveryPoint,
          status: 'success',
        }]);

        onRecoverySuccess?.(result);
        onClose();
      } else {
        throw new Error(result.error || 'Recovery failed');
      }
    } catch (error) {
      console.error('Recovery failed:', error);

      // Add to recovery history
      setRecoveryHistory((prev) => [...prev, {
        timestamp: new Date().toISOString(),
        recoveryPoint: selectedRecoveryPoint,
        status: 'failed',
        error: error.message,
      }]);
    } finally {
      setRecovering(false);
    }
  };

  const handleRetry = () => {
    onRetry?.();
    onClose();
  };

  const severity = getErrorSeverity(error);
  const category = getErrorCategory(error);
  const suggestions = getSuggestedActions(error);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="error-recovery-modal"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: {
          xs: '95%', sm: '80%', md: '70%', lg: '60%',
        },
        maxWidth: '800px',
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        overflow: 'auto',
      }}
      >
        {/* Header */}
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <ErrorIcon color={severity} />
            <Box>
              <Typography variant="h5" component="h2">
                Flow Execution Error
              </Typography>
              <Chip
                label={category}
                color={severity}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Stack>
        </Box>

        {/* Error Details */}
        <Box sx={{ p: 3 }}>
          <Alert severity={severity} sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {error || 'An unexpected error occurred during flow execution.'}
            </Typography>
          </Alert>

          {/* Suggested Actions */}
          {suggestions.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">💡 Suggested Actions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {suggestions.map((suggestion, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={suggestion} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Recovery Options */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">🔄 Recovery Options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Choose a recovery point to resume your flow execution:
                </Typography>

                {recoveryOptions.map((option) => (
                  <Box
                    key={option.id}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: selectedRecoveryPoint?.id === option.id ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: selectedRecoveryPoint?.id === option.id ? 'primary.50' : 'transparent',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    onClick={() => setSelectedRecoveryPoint(option)}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <TimelineIcon color={selectedRecoveryPoint?.id === option.id ? 'primary' : 'action'} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                          {option.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(option.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                      {option.recommended && (
                        <Chip label="Recommended" color="primary" size="small" />
                      )}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* Recovery History */}
          {recoveryHistory.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">📊 Recovery History</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {recoveryHistory.map((attempt, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        {attempt.status === 'success'
                          ? <CheckCircleIcon color="success" fontSize="small" />
                          : <CancelIcon color="error" fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={`${attempt.recoveryPoint.description}`}
                        secondary={(
                          <>
                            {new Date(attempt.timestamp).toLocaleString()}
                            {attempt.error && ` - ${attempt.error}`}
                          </>
                        )}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        {/* Actions */}
        <Divider />
        <Box sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={recovering}>
              Cancel
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
              disabled={recovering}
            >
              Retry from Beginning
            </Button>

            <Button
              variant="contained"
              startIcon={recovering ? <CircularProgress size={20} /> : <RestoreIcon />}
              onClick={handleRecovery}
              disabled={!selectedRecoveryPoint || recovering}
            >
              {recovering ? 'Recovering...' : 'Recover from Checkpoint'}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
}

export default ErrorRecoveryModal;
